import { useEffect, useRef, useState } from "react";

import toast from "react-hot-toast";

import { useCustomerViewModel } from "../../../viewmodels/operation/customer/useCustomerViewModel";

import { useCalibrationCertificateViewModel } from "../../../viewmodels/operation/calibrationcertificate/useCalibrationCertificateViewModel";
import { useServiceCallViewModel } from "../../../viewmodels/operation/servicecall/useServiceCallViewModel";

import ScaleMakeAsyncSelect from "../../../components/common/ScaleMakeAsyncSelect";

import ScaleModelAsyncSelect from "../../../components/common/ScaleModelAsyncSelect";

type Props = {
  id: string;
  onClose: () => void;
  onSaved: () => void;
};

const EditCalibrationCertificateForm = ({ id, onClose, onSaved }: Props) => {
  const [customerSelected, setCustomerSelected] = useState(false);

  const [selectedServiceCallId, setSelectedServiceCallId] = useState("");

  const {
    serviceCalls,
    setSearch: setServiceCallSearch,
    getServiceCallById,
  } = useServiceCallViewModel();

  const { getCalibrationCertificateById, updateCalibrationCertificate } =
    useCalibrationCertificateViewModel();

  const { customers, setSearch } = useCustomerViewModel();

  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  const customerDropdownRef = useRef<any>(null);

  const [form, setForm] = useState<any>(null);

  const [lineItems, setLineItems] = useState<any[]>([]);

  const getNumber = (value: string) =>
    parseFloat(String(value).replace(/[^\d.-]/g, "")) || 0;

  // ================= OUTSIDE CLICK =================
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        customerDropdownRef.current &&
        !customerDropdownRef.current.contains(event.target)
      ) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!form?.date || !form?.frequency) return;

    console.log("Date:", form.date);
    console.log("Frequency:", form.frequency);

    const due = new Date(form.date);

    if (Number.isNaN(due.getTime())) {
      console.error("Invalid date:", form.date);
      return;
    }

    const months = Number(form.frequency);

    if (Number.isNaN(months)) {
      console.error("Invalid frequency:", form.frequency);
      return;
    }

    due.setMonth(due.getMonth() + months);

    setForm((prev: any) => ({
      ...prev,
      dueDate: due.toISOString().split("T")[0],
    }));
  }, [form?.date, form?.frequency]);

  const parseCapacity = (capacity: string) => {
    if (!capacity)
      return {
        capacityValue: "",

        division: "",

        capacityUnit: "",
      };

    const match = capacity.match(/^([\d.]+)([a-zA-Z]+)x([\d.]+)([a-zA-Z]+)$/);

    if (!match)
      return {
        capacityValue: "",

        division: "",

        capacityUnit: "",
      };

    return {
      capacityValue: match[1],

      capacityUnit: match[2],

      division: match[3],
    };
  };

  const getDecimalPlaces = (value: string) => {
    if (!value.includes(".")) return 0;
    return value.split(".")[1].length;
  };

  const removeUnit = (value: string) =>
    String(value).replace(/\s*[a-zA-Z]+$/, "");

  const generateCalibrationRows = (
    capacityValue: number,
    unit: string,
    division: string,
  ) => {
    if (!capacityValue || !unit) return;

    const decimalPlaces = getDecimalPlaces(division || "0");

    const loads = [
      0,
      capacityValue * 0.25,
      capacityValue * 0.5,
      capacityValue * 0.75,
      capacityValue,
    ];

    setLineItems((prev) => {
      // If rows already exist, update them
      if (prev.length > 0) {
        return prev.map((row: any, index: number) => {
          const load = loads[index] ?? getNumber(row.loadApplied);

          const before =
            row.beforeCalibration !== ""
              ? getNumber(row.beforeCalibration)
              : load;

          const after =
            row.afterCalibration !== ""
              ? getNumber(row.afterCalibration)
              : load;

          return {
            ...row,
            loadApplied: load.toFixed(decimalPlaces),

            // If user hasn't edited these, keep them synced with Load Applied
            beforeCalibration:
              before === getNumber(row.loadApplied)
                ? load.toFixed(decimalPlaces)
                : before.toFixed(decimalPlaces),

            afterCalibration:
              after === getNumber(row.loadApplied)
                ? load.toFixed(decimalPlaces)
                : after.toFixed(decimalPlaces),

            beforeCalibrationError: (before - load).toFixed(decimalPlaces),
            afterCalibrationError: (after - load).toFixed(decimalPlaces),
          };
        });
      }

      // Create default rows
      return loads.map((load, index) => ({
        id: crypto.randomUUID(),
        lineNo: index + 1,
        loadApplied: load.toFixed(decimalPlaces),
        beforeCalibration: load.toFixed(decimalPlaces),
        beforeCalibrationError: (0).toFixed(decimalPlaces),
        afterCalibration: load.toFixed(decimalPlaces),
        afterCalibrationError: (0).toFixed(decimalPlaces),
      }));
    });
  };

  // ================= LOAD =================
  const loadData = async () => {
    if (!id) return;

    const data = await getCalibrationCertificateById(id);

    if (data) {
      setForm({
        id: data.id || "",
        no: data.no || "",
        date: data.date ? data.date.split("T")[0] : "",

        customerName: data.customerName || "",
        customerEmail: data.customerEmail || "",
        scaleLocation: data.scaleLocation || "",
        contactPerson: data.contactPerson || "",
        contactPhoneNumber: data.contactPhoneNumber || "",
        customerAddress: data.customerAddress || "",

        reportNo: data.reportNo || "",

        makeId: data.makeId || "",
        make: data.make || "",

        modelId: data.modelId || "",
        model: data.model || "",

        serialNumber: data.serialNumber || "",

        ...parseCapacity(data.capacity || ""),

        capacity: data.capacity || "",

        // IMPORTANT
        scaleId: data.scaleId || null,

        nmiNumber: data.nmiNumber || "",

        identifier: data.identifier || "",

        beforeCalibration: Number(data.beforeCalibration ?? 1),
        afterCalibration: Number(data.afterCalibration ?? 1),

        scaleCertified: Number(data.scaleCertified ?? 1),
        scaleUsedForTrade: Number(data.scaleUsedForTrade ?? 1),

        comments: data.comments || "",
        frequency: data.frequency || "",
        maximumPermissibleError: data.maximumPermissibleError || "",

        verificationCode: data.verificationCode || "",

        verificationDate: data.verificationDate
          ? data.verificationDate.split("T")[0]
          : "",

        calibratedBy: data.calibratedBy || "",

        calibratedDate: data.calibratedDate
          ? data.calibratedDate.split("T")[0]
          : "",

        dueDate: data.dueDate ? data.dueDate.split("T")[0] : "",
      });
      setSelectedServiceCallId(data.serviceCallId || "");

      setCustomerSelected(true);

      setServiceCallSearch(data.customerName);

      setLineItems(
        (data.lineItems || []).map((x: any) => ({
          ...x,
          loadApplied: removeUnit(x.loadApplied),
          beforeCalibration: removeUnit(x.beforeCalibration),
          beforeCalibrationError: removeUnit(x.beforeCalibrationError),
          afterCalibration: removeUnit(x.afterCalibration),
          afterCalibrationError: removeUnit(x.afterCalibrationError),
        })),
      );
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  // ================= SELECT CUSTOMER =================
  const handleSelectCustomer = (customer: any) => {
    const firstAddress = customer?.addresses?.[0];

    const fullAddress = [
      firstAddress?.addressLine1,
      firstAddress?.addressLine2,
      firstAddress?.city,
      firstAddress?.state,
      firstAddress?.postalCode,
    ]
      .filter(Boolean)
      .join(", ");

    const firstPhone = customer?.customerPhoneNumbers?.[0];

    setForm((prev: any) => ({
      ...prev,

      customerName: customer.name,
      customerEmail: customer.email,

      contactPerson: firstPhone?.name || "",
      contactPhoneNumber: firstPhone?.phoneNumber || "",
      customerAddress: fullAddress,

      serviceCallId: "",
    }));

    setSelectedServiceCallId("");

    setCustomerSelected(true);

    setServiceCallSearch(customer.name);

    setShowCustomerDropdown(false);
  };

  if (!form) return null;

  // ================= ADD ROW =================
  const addRow = () => {
    setLineItems([
      ...lineItems,

      {
        id: crypto.randomUUID(),

        loadApplied: "",

        beforeCalibration: "",

        beforeCalibrationError: "",

        afterCalibration: "",

        afterCalibrationError: "",
      },
    ]);
  };

  // ================= REMOVE ROW =================
  const removeRow = (index: number) => {
    const updated = [...lineItems];

    updated.splice(index, 1);

    setLineItems(updated);
  };

  // ================= SAVE =================
  const handleSubmit = async (e: any) => {
    e.preventDefault();

    const payload = {
      id: form.id,

      serviceCallId: form.serviceCallId || null,

      no: Number(form.no || 0),

      date: form.date ? new Date(form.date).toISOString() : null,

      customerName: form.customerName || "",

      customerEmail: form.customerEmail || "",

      scaleLocation: form.scaleLocation || "",

      contactPerson: form.contactPerson || "",

      contactPhoneNumber: form.contactPhoneNumber || "",

      customerAddress: form.customerAddress || "",

      reportNo: form.reportNo || "",

      make: form.make || "",

      model: form.model || "",

      serialNumber: form.serialNumber || "",

      capacity: form.capacity || "",

      scaleId: form.scaleId || null,

      nmiNumber: form.nmiNumber || "",

      beforeCalibration: Number(form.beforeCalibration || 0),

      afterCalibration: Number(form.afterCalibration || 0),

      comments: form.comments || "",

      frequency: form.frequency || "",

      maximumPermissibleError: form.maximumPermissibleError || "",

      verificationCode: form.verificationCode || "",

      scaleCertified: Number(form.scaleCertified || 0),

      scaleUsedForTrade: Number(form.scaleUsedForTrade || 0),

      verificationDate: form.verificationDate
        ? new Date(form.verificationDate).toISOString()
        : null,

      calibratedBy: form.calibratedBy || "",

      calibratedDate: form.calibratedDate
        ? new Date(form.calibratedDate).toISOString()
        : null,

      dueDate: form.dueDate ? new Date(form.dueDate).toISOString() : null,

      identifier: form.identifier || "",

      lineItems: lineItems.map((x: any, index: number) => ({
        id: x.id || crypto.randomUUID(),

        // IMPORTANT
        lineNumber: index + 1,

        loadApplied: `${removeUnit(x.loadApplied)} ${form.capacityUnit}`,

        beforeCalibration: `${removeUnit(x.beforeCalibration)} ${form.capacityUnit}`,

        beforeCalibrationError: `${removeUnit(x.beforeCalibrationError)} ${form.capacityUnit}`,

        afterCalibration: `${removeUnit(x.afterCalibration)} ${form.capacityUnit}`,

        afterCalibrationError: `${removeUnit(x.afterCalibrationError)} ${form.capacityUnit}`,
      })),
    };

    const success = await updateCalibrationCertificate(payload);

    if (success) {
      toast.success("Updated successfully");
      onSaved();
      onClose();
    }
  };

  return (
    <div className="p-4">
      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-white border-2 border-slate-200 rounded-3xl p-8">
          <div>
            <label className="block font-semibold mb-2">Certificate No</label>

            <input
              type="number"
              value={form.no}
              onChange={(e) =>
                setForm({
                  ...form,
                  no: e.target.value,
                })
              }
              className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
            />
          </div>
        </div>

        {/* ================= CUSTOMER DETAILS ================= */}
        <div className="bg-white border-2 border-slate-200 rounded-3xl p-8">
          <h2 className="text-2xl font-bold mb-8">Customer Details</h2>

          <div className="space-y-5">
            <div className="grid grid-cols-4 md:grid-cols-2 gap-8 mt-4">
              <div className="relative" ref={customerDropdownRef}>
                <label className="block text-[15px] font-semibold mb-2">
                  Customer Name
                </label>

                <input
                  value={form.customerName}
                  onFocus={() => setShowCustomerDropdown(true)}
                  onChange={(e) => {
                    setForm({
                      ...form,

                      customerName: e.target.value,
                    });

                    setSearch(e.target.value);

                    setShowCustomerDropdown(true);
                  }}
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                />

                {showCustomerDropdown && (
                  <div className="absolute z-50 bg-white border rounded-2xl shadow-xl w-full mt-2 max-h-72 overflow-auto">
                    {customers.map((x: any) => (
                      <button
                        key={x.id}
                        type="button"
                        onClick={() => handleSelectCustomer(x)}
                        className="w-full text-left px-4 py-4 border-b hover:bg-slate-100"
                      >
                        {x.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-[15px] font-semibold mb-2">
                  Service Call
                </label>
                <select
                  disabled={!customerSelected}
                  value={selectedServiceCallId}
                  onChange={async (e) => {
                    const id = e.target.value;

                    setSelectedServiceCallId(id);

                    if (!id) return;

                    const serviceCall = await getServiceCallById(id);

                    setForm((prev: any) => ({
                      ...prev,
                      serviceCallId: id,
                    }));

                    if (!serviceCall) return;

                    const scale = serviceCall?.serviceCallScales?.[0];

                    if (!scale) return;

                    const parsed = parseCapacity(scale.capacity);

                    setForm((prev: any) => ({
                      ...prev,

                      serviceCallId: id,

                      // IMPORTANT
                      scaleId: scale.id || null,

                      make: scale.make,
                      model: scale.model,
                      serialNumber: scale.serialNumber,

                      capacity: scale.capacity,

                      capacityValue: parsed.capacityValue,
                      division: parsed.division,
                      capacityUnit: parsed.capacityUnit,
                    }));

                    generateCalibrationRows(
                      Number(parsed.capacityValue),
                      parsed.capacityUnit,
                      parsed.division,
                    );
                  }}
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                >
                  <option value="">Select Service Call</option>
                  {!customerSelected && (
                    <option value="">Select customer first</option>
                  )}

                  {customerSelected &&
                    serviceCalls.map((x: any) => (
                      <option key={x.id} value={x.id}>
                        {x.no}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-4 md:grid-cols-4 gap-8 mt-4">
              <div>
                <label className="block font-semibold mb-2">
                  Customer Email
                </label>

                <input
                  value={form.customerEmail}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      customerEmail: e.target.value,
                    })
                  }
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                />
              </div>

              <div>
                <label className="block font-semibold mb-2">
                  Contact Person
                </label>

                <input
                  value={form.contactPerson}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      contactPerson: e.target.value,
                    })
                  }
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                />
              </div>

              <div>
                <label className="block font-semibold mb-2">
                  Contact Phone Number
                </label>

                <input
                  value={form.contactPhoneNumber}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      contactPhoneNumber: e.target.value,
                    })
                  }
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                />
              </div>
            </div>

            <div>
              <label className="block text-[15px] font-semibold mb-2 mt-4">
                Customer Address
              </label>

              <textarea
                rows={4}
                value={form.customerAddress}
                onChange={(e) =>
                  setForm({
                    ...form,
                    customerAddress: e.target.value,
                  })
                }
                className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
              />
            </div>
          </div>
        </div>

        {/* ================= SCALE DETAILS ================= */}
        <div className="bg-white border-2 border-slate-200 rounded-3xl p-8">
          <h2 className="text-2xl font-bold mb-6">Scale Details</h2>

          <div className="grid grid-cols-4 md:grid-cols-4 gap-8 mt-4">
            {/* REPORT NO */}
            <div>
              <label className="block font-semibold mb-2">Report No</label>

              <input
                readOnly
                value={form.reportNo}
                onChange={(e) =>
                  setForm({
                    ...form,
                    reportNo: e.target.value,
                  })
                }
                className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
              />
            </div>
            {/* MAKE */}
            <div>
              <label className="block font-semibold mb-2">Make</label>

              <ScaleMakeAsyncSelect
                value={
                  form.make
                    ? {
                        value: form.makeId || form.make,
                        label: form.make,
                        __isNew__: !form.makeId,
                      }
                    : null
                }
                onChange={(selected: any) => {
                  setForm((prev: any) => ({
                    ...prev,

                    makeId: selected?.__isNew__ ? null : selected?.value,
                    make: selected?.label ?? "",
                  }));
                }}
              />
            </div>

            {/* MODEL */}
            <div>
              <label className="block font-semibold mb-2">Model</label>

              <ScaleModelAsyncSelect
                value={
                  form.model
                    ? {
                        value: form.modelId || form.model,
                        label: form.model,
                        __isNew__: !form.modelId,
                      }
                    : null
                }
                onChange={(selected: any) => {
                  if (!selected) {
                    setForm((prev: any) => ({
                      ...prev,
                      modelId: "",
                      model: "",
                    }));
                    return;
                  }

                  const capacity = selected.capacities?.[0];

                  const capacityUnit = capacity?.capacityUnitName ?? "kg";

                  const divisionUnit =
                    capacity?.divisionUnitName ?? capacityUnit;

                  setForm((prev: any) => ({
                    ...prev,

                    modelId: selected.__isNew__ ? null : selected.value,
                    model: selected.label,

                    capacityValue:
                      capacity?.capacity?.toString() ?? prev.capacityValue,

                    division: capacity?.division?.toString() ?? prev.division,

                    capacityUnit,

                    capacity: capacity
                      ? `${capacity.capacity}${capacityUnit}x${capacity.division}${divisionUnit}`
                      : prev.capacity,
                  }));

                  if (capacity?.capacity) {
                    generateCalibrationRows(
                      Number(capacity.capacity),
                      capacityUnit,
                      capacity.division?.toString() ?? "0",
                    );
                  }
                }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-1 gap-8 mt-4">
            {/* CAPACITY */}
            <div>
              <label className="block font-semibold mb-2">Capacity</label>

              <div className="grid grid-cols-3 gap-3">
                <input
                  type="number"
                  placeholder="3000"
                  value={form.capacityValue || ""}
                  onChange={(e) => {
                    const value = e.target.value;

                    const updatedForm = {
                      ...form,

                      capacityValue: value,

                      capacity:
                        value && form.division && form.capacityUnit
                          ? `${value}${form.capacityUnit}x${form.division}${form.capacityUnit}`
                          : "",
                    };

                    setForm(updatedForm);

                    if (value && form.capacityUnit) {
                      generateCalibrationRows(
                        Number(value),
                        form.capacityUnit,
                        form.division,
                      );
                    }
                  }}
                  className="border-2 border-slate-300 rounded-2xl px-4 py-4"
                />

                <input
                  type="number"
                  step="any"
                  placeholder="0.5"
                  value={form.division || ""}
                  onChange={(e) => {
                    const division = e.target.value;

                    setForm({
                      ...form,

                      division,

                      capacity:
                        form.capacityValue && division && form.capacityUnit
                          ? `${form.capacityValue}${form.capacityUnit}x${division}${form.capacityUnit}`
                          : "",
                    });
                    if (form.capacityValue && form.capacityUnit) {
                      generateCalibrationRows(
                        Number(form.capacityValue),
                        form.capacityUnit,
                        division,
                      );
                    }
                  }}
                  className="border-2 border-slate-300 rounded-2xl px-4 py-4"
                />

                <select
                  value={form.capacityUnit || ""}
                  onChange={(e) => {
                    const unit = e.target.value;

                    const updatedForm = {
                      ...form,

                      capacityUnit: unit,

                      capacity:
                        form.capacityValue && form.division
                          ? `${form.capacityValue}${unit}x${form.division}${unit}`
                          : "",
                    };

                    setForm(updatedForm);

                    if (form.capacityValue && Number(form.capacityValue) > 0) {
                      generateCalibrationRows(
                        Number(form.capacityValue),
                        unit,
                        form.division,
                      );
                    }
                  }}
                  className="border-2 border-slate-300 rounded-2xl px-4 py-4"
                >
                  <option value="">Unit</option>

                  <option value="kg">kg</option>

                  <option value="g">g</option>

                  <option value="mg">mg</option>

                  <option value="t">t</option>

                  <option value="lb">lb</option>

                  <option value="oz">oz</option>
                </select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mt-4">
            {/* SERIAL NUMBER */}
            <div>
              <label className="block font-semibold mb-2">Serial Number</label>

              <input
                value={form.serialNumber}
                onChange={(e) =>
                  setForm({
                    ...form,
                    serialNumber: e.target.value.toUpperCase(),
                  })
                }
                className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
              />
            </div>
            <div>
              <label className="block font-semibold mb-2">NMI Number</label>

              <input
                value={form.nmiNumber}
                onChange={(e) =>
                  setForm({
                    ...form,
                    nmiNumber: e.target.value,
                  })
                }
                className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
              />
            </div>

            <div>
              <label className="block font-semibold mb-2">Id</label>

              <input
                value={form.identifier}
                onChange={(e) =>
                  setForm({
                    ...form,
                    identifier: e.target.value,
                  })
                }
                className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
              />
            </div>
            <div>
              <label className="block font-semibold mb-2">Scale Location</label>

              <input
                value={form.scaleLocation}
                onChange={(e) =>
                  setForm({
                    ...form,
                    scaleLocation: e.target.value,
                  })
                }
                className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
              />
            </div>
          </div>
        </div>

        {/* ================= LINE ITEMS ================= */}
        <div className="bg-white border-2 border-slate-200 rounded-3xl p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Line Items</h2>

            <button
              type="button"
              onClick={addRow}
              className="bg-primary text-white px-5 py-3 rounded-2xl"
            >
              + Add Row
            </button>
          </div>

          <table className="w-full border">
            <thead className="bg-slate-100">
              <tr>
                <th className="border p-3">Load Applied</th>

                <th className="border p-3">Before Calibration</th>

                <th className="border p-3">Error (+/-)</th>

                <th className="border p-3">After Calibration</th>

                <th className="border p-3">Error (+/-)</th>

                <th className="border p-3">Action</th>
              </tr>
            </thead>

            <tbody>
              {lineItems.map((item, index) => (
                <tr key={item.id}>
                  {[
                    "loadApplied",
                    "beforeCalibration",
                    "beforeCalibrationError",
                    "afterCalibration",
                    "afterCalibrationError",
                  ].map((field) => (
                    <td key={field} className="border p-2">
                      <input
                        value={item[field] ?? ""}
                        readOnly={false}
                        onChange={(e) => {
                          const updated = [...lineItems];

                          updated[index][field] = e.target.value;

                          const decimalPlaces = getDecimalPlaces(
                            form.division || "0",
                          );

                          if (field === "loadApplied") {
                            // Keep Before & After equal to Load Applied
                            updated[index].beforeCalibration =
                              updated[index].loadApplied;
                            updated[index].afterCalibration =
                              updated[index].loadApplied;
                          }

                          const load = getNumber(updated[index].loadApplied);
                          const before = getNumber(
                            updated[index].beforeCalibration,
                          );
                          const after = getNumber(
                            updated[index].afterCalibration,
                          );

                          updated[index].beforeCalibrationError = (
                            before - load
                          ).toFixed(decimalPlaces);

                          updated[index].afterCalibrationError = (
                            after - load
                          ).toFixed(decimalPlaces);

                          setLineItems(updated);
                        }}
                        className={`w-full border rounded-xl px-3 py-2`}
                      />
                    </td>
                  ))}

                  <td className="border p-2">
                    <button
                      type="button"
                      onClick={() => removeRow(index)}
                      className="bg-red-500 text-white px-4 py-2 rounded-xl"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white border-2 border-slate-200 rounded-3xl p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block font-semibold mb-2">
                Before Calibration
              </label>

              <select
                value={form.beforeCalibration}
                onChange={(e) =>
                  setForm({
                    ...form,
                    beforeCalibration: Number(e.target.value),
                  })
                }
                className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
              >
                <option value={0}>Pass</option>
                <option value={1}>Fail</option>
              </select>
            </div>

            <div>
              <label className="block font-semibold mb-2">
                After Calibration
              </label>

              <select
                value={form.afterCalibration}
                onChange={(e) =>
                  setForm({
                    ...form,
                    afterCalibration: Number(e.target.value),
                  })
                }
                className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
              >
                <option value={0}>Pass</option>
                <option value={1}>Fail</option>
              </select>
            </div>
          </div>
        </div>

        {/* ================= COMMENTS ================= */}
        <div className="bg-white border-2 border-slate-200 rounded-3xl p-8">
          <h2 className="text-2xl font-bold mb-5">Comments</h2>

          <textarea
            rows={6}
            value={form.comments}
            onChange={(e) =>
              setForm({
                ...form,
                comments: e.target.value,
              })
            }
            className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
          />
        </div>

        {/* ================= CALIBRATION DETAILS ================= */}
        <div className="bg-white border-2 border-slate-200 rounded-3xl p-8">
          <h2 className="text-2xl font-bold mb-6">Calibration Details</h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            <div>
              <label className="block font-semibold mb-2">
                Frequency(Months)
              </label>

              <input
                type="number"
                min="0"
                value={form.frequency}
                onChange={(e) =>
                  setForm({
                    ...form,
                    frequency: e.target.value,
                  })
                }
                className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
              />
            </div>
            <div>
              <label className="block font-semibold mb-2">
                Max. Perm. Error
              </label>

              <input
                value={form.maximumPermissibleError}
                onChange={(e) =>
                  setForm({
                    ...form,
                    maximumPermissibleError: e.target.value,
                  })
                }
                className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
              />
            </div>
            <div>
              <label className="block font-semibold mb-2">
                Verification Code
              </label>

              <input
                value={form.verificationCode}
                onChange={(e) =>
                  setForm({
                    ...form,
                    verificationCode: e.target.value.toUpperCase(),
                  })
                }
                className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
              />
            </div>
            <div>
              <label className="block font-semibold mb-2">
                Scale Certified
              </label>

              <select
                value={form.scaleCertified}
                onChange={(e) =>
                  setForm({
                    ...form,
                    scaleCertified: Number(e.target.value),
                  })
                }
                className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
              >
                <option value={0}>Yes</option>
                <option value={1}>No</option>
              </select>
            </div>
            <div>
              <label className="block font-semibold mb-2">
                Scale Used For Trade
              </label>

              <select
                value={form.scaleUsedForTrade}
                onChange={(e) =>
                  setForm({
                    ...form,
                    scaleUsedForTrade: Number(e.target.value),
                  })
                }
                className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
              >
                <option value={0}>Yes</option>
                <option value={1}>No</option>
              </select>
            </div>
            <div>
              <label className="block font-semibold mb-2">
                Verification Date
              </label>

              <input
                type="date"
                value={form.verificationDate}
                onChange={(e) =>
                  setForm({
                    ...form,
                    verificationDate: e.target.value,
                  })
                }
                className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
              />
            </div>
            {/*  <div>
                <label className="block font-semibold mb-2">
                  Calibrated Date
                </label>

                <input
                  type="date"
                  value={form.calibratedDate}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      calibratedDate: e.target.value,
                    })
                  }
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                />
              </div> */}
            <div>
              <label className="block font-semibold mb-2">Calibrated By</label>

              <input
                value={form.calibratedBy}
                onChange={(e) =>
                  setForm({
                    ...form,
                    calibratedBy: e.target.value.toUpperCase(),
                  })
                }
                className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
              />
            </div>
            <div>
              <label className="block font-semibold mb-2">Date</label>

              <input
                type="date"
                value={form.date}
                onChange={(e) =>
                  setForm({
                    ...form,
                    date: e.target.value,
                  })
                }
                className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
              />
            </div>
            <div>
              <label className="block font-semibold mb-2">Due Date</label>

              <input
                type="date"
                value={form.dueDate}
                onChange={(e) =>
                  setForm({
                    ...form,
                    dueDate: e.target.value,
                  })
                }
                className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
              />
            </div>
          </div>
        </div>

        {/* SAVE */}
        <div className="flex justify-end">
          <button
            type="submit"
            className="px-8 py-3 rounded-2xl bg-primary text-white font-semibold"
          >
            Update Calibration Certificate
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditCalibrationCertificateForm;
